import React, { createContext, useContext, useState } from 'react';

import type { BrandingTheme } from '@atlassian/csm-widget-ui-components';

type SupportWidgetConfig = {
	isEnabled?: boolean;
	site?: string;
	siteId?: string;
	widgetId: string;
	widgetPosition?: 'CENTER' | 'CORNER';
	widgetTheme: BrandingTheme;
};

type AiWidgetConfigContextType = {
	supportWidgetConfig: SupportWidgetConfig;
};

const AiWidgetConfigContext = createContext<AiWidgetConfigContextType | null>(null);

/**
 * Provides the `AiWidgetConfigContext` value to the children
 * Value is stored on initial render, so if you change the value, updating the props will not update the context value
 */
export const AiWidgetConfigProvider = ({
	supportWidgetConfig,
	children,
}: {
	children: React.ReactNode;
	supportWidgetConfig: AiWidgetConfigContextType['supportWidgetConfig'];
}): React.JSX.Element => {
	const [value] = useState<AiWidgetConfigContextType>({
		supportWidgetConfig,
	});

	return <AiWidgetConfigContext.Provider value={value}>{children}</AiWidgetConfigContext.Provider>;
};

/**
 * Provides the `AiWidgetConfigContext` value if it exists, otherwise return null
 * Use this hook if you want to access the `AiWidgetConfigContext` value in a component
 * but have a fallback behaviour if its missing
 */
export const useOptionalAiWidgetConfig = () => {
	return useContext(AiWidgetConfigContext);
};

/**
 * Provides the `AiWidgetConfigContext` value if it exists, otherwise throws an error
 * Use this hook if you need to access the `AiWidgetConfigContext` value in a component
 * and should fail fast if its missing
 */
export const useAiWidgetConfig = () => {
	const contextValue = useContext(AiWidgetConfigContext);

	if (!contextValue) {
		throw new Error('useSuppportWidgetConfig must be used within a AiWidgetConfigProvider');
	}

	return contextValue;
};
